import { liveQuery } from "dexie";
import { useEffect, useState } from "react";

import type { PosSalesLayoutCardAspectRatio } from "../runtime/gateway/sales-layout-client";
import { readLocalSalesLayoutCardAspectRatio } from "../runtime/local-store/sales-layout-runtime";
import type { TrustedTerminalIdentity } from "../runtime/local-store/terminal-state";

export function SalesLayoutCardPresentationRuntime({
  identity,
}: {
  identity: TrustedTerminalIdentity | null;
}) {
  const [aspectRatio, setAspectRatio] = useState<PosSalesLayoutCardAspectRatio | null>(null);

  useEffect(() => {
    if (!identity) {
      setAspectRatio(null);
      return;
    }

    const subscription = liveQuery(() => readLocalSalesLayoutCardAspectRatio(identity)).subscribe({
      next: setAspectRatio,
      error: () => setAspectRatio(null),
    });
    return () => subscription.unsubscribe();
  }, [identity?.organization_id, identity?.branch_id, identity?.node_id]);

  useEffect(() => {
    if (!aspectRatio || aspectRatio.width <= 0 || aspectRatio.height <= 0) return;

    const heightPerWidth = aspectRatio.height / aspectRatio.width;
    let observedGrid: HTMLElement | null = null;
    let animationFrame = 0;

    const applyPublishedRowGeometry = () => {
      if (!observedGrid) return;
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(() => {
        if (!observedGrid) return;
        const firstCard = observedGrid.querySelector<HTMLElement>(".sale-product-card");
        if (!firstCard) {
          observedGrid.style.removeProperty("grid-auto-rows");
          return;
        }

        const cardWidth = firstCard.getBoundingClientRect().width;
        if (!Number.isFinite(cardWidth) || cardWidth <= 0) return;

        observedGrid.style.gridAutoRows = `${cardWidth * heightPerWidth}px`;
      });
    };

    const resizeObserver = new ResizeObserver(applyPublishedRowGeometry);

    const bindCurrentGrid = () => {
      const nextGrid = document.querySelector<HTMLElement>(".sale-product-grid");
      if (nextGrid === observedGrid) {
        applyPublishedRowGeometry();
        return;
      }

      if (observedGrid) {
        resizeObserver.unobserve(observedGrid);
        observedGrid.style.removeProperty("grid-auto-rows");
      }

      observedGrid = nextGrid;
      if (!observedGrid) return;

      resizeObserver.observe(observedGrid);
      applyPublishedRowGeometry();
    };

    const mutationObserver = new MutationObserver(bindCurrentGrid);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    bindCurrentGrid();

    return () => {
      cancelAnimationFrame(animationFrame);
      mutationObserver.disconnect();
      resizeObserver.disconnect();
      if (observedGrid) observedGrid.style.removeProperty("grid-auto-rows");
    };
  }, [aspectRatio?.width, aspectRatio?.height]);

  if (!aspectRatio) return null;

  return (
    <style>{`
      .sale-product-grid {
        align-items: stretch;
      }

      .sale-product-card {
        width: 100%;
        height: 100%;
        min-height: 0;
        align-self: stretch;
        box-sizing: border-box;
        aspect-ratio: auto;
        overflow: hidden;
      }
    `}</style>
  );
}
